/**
 * utils/dateHelpers.ts
 * Date and time utilities
 * 
 * Handles timezone conversions, trading hours calculations, and date formatting
 * for the trading journal. All dates are stored as ISO strings in UTC but
 * displayed in the user's configured DISPLAY timezone (from BasicSettings).
 * 
 * IMPORTANT: These functions use the user's DISPLAY timezone from settings,
 * NOT the browser's local timezone. A trader in California might set their
 * display timezone to "America/New_York" to see Eastern trading hours.
 * 
 * Key patterns:
 * - Storage: Always UTC ISO strings ("2024-08-11T14:30:00.000Z")
 * - Display: User's configured display timezone with proper formatting
 * - Session dates: YYYY-MM-DD format in display timezone
 * - Trading hours: Display timezone-aware calculations
 */

/**
 * Get current date/time as UTC ISO string for database storage
 * 
 * @returns ISO string in UTC (e.g., "2024-08-11T14:30:00.000Z")
 */
export function nowUTC(): string {
  return new Date().toISOString();
}

/**
 * Convert user timezone date/time to UTC ISO string for storage
 * 
 * @param dateInput - Date string, Date object, or timestamp
 * @param userTimezone - User's timezone (e.g., "America/New_York")
 * @returns ISO string in UTC
 * 
 * @example
 * // User enters "2024-08-11 09:30" in EST
 * toUTC("2024-08-11 09:30", "America/New_York") 
 * // Returns: "2024-08-11T13:30:00.000Z" (UTC)
 */
export function toUTC(dateInput: string | Date | number, userTimezone: string): string {
  let date: Date;
  
  if (dateInput instanceof Date) {
    date = dateInput;
  } else if (typeof dateInput === 'number') {
    date = new Date(dateInput);
  } else {
    // Parse string input
    date = new Date(dateInput);
  }
  
  if (!isValidDate(date)) {
    throw new Error(`Invalid date input: ${dateInput}`);
  }
  
  // If the input doesn't specify timezone, assume it's in user's timezone
  if (typeof dateInput === 'string' && !dateInput.includes('Z') && !dateInput.includes('+')) {
    
    // Parse the user input as if it's in their timezone
    try {
      const tempDate = new Date(dateInput);
      const utcTime = tempDate.getTime();
      const timezoneOffset = getTimezoneOffset(userTimezone, tempDate);
      return new Date(utcTime + timezoneOffset).toISOString();
    } catch (_error) {
      // Fallback to simple parsing
      return date.toISOString();
    }
  }
  
  return date.toISOString();
}

/**
 * Convert UTC ISO string to user's timezone for display
 * 
 * @param utcISOString - UTC ISO string from database
 * @param userTimezone - User's timezone (e.g., "America/New_York")
 * @param format - Output format type
 * @returns Formatted date string in user's timezone
 * 
 * @example
 * fromUTC("2024-08-11T13:30:00.000Z", "America/New_York", "datetime")
 * // Returns: "2024-08-11 09:30:00" (EST)
 */
export function fromUTC(
  utcISOString: string, 
  userTimezone: string, 
  format: 'date' | 'time' | 'datetime' | 'display' = 'display'
): string {
  const date = new Date(utcISOString);
  
  if (!isValidDate(date)) {
    throw new Error(`Invalid ISO string: ${utcISOString}`);
  }
  
  const options: Intl.DateTimeFormatOptions = {
    timeZone: userTimezone
  };
  
  switch (format) {
    case 'date':
      options.year = 'numeric';
      options.month = '2-digit';
      options.day = '2-digit';
      return new Intl.DateTimeFormat('en-CA', options).format(date); // YYYY-MM-DD
      
    case 'time':
      options.hour = '2-digit';
      options.minute = '2-digit';
      options.second = '2-digit';
      options.hour12 = false;
      return new Intl.DateTimeFormat('en-GB', options).format(date); // HH:mm:ss
      
    case 'datetime':
      options.year = 'numeric';
      options.month = '2-digit';
      options.day = '2-digit';
      options.hour = '2-digit';
      options.minute = '2-digit';
      options.second = '2-digit';
      options.hour12 = false;
      return new Intl.DateTimeFormat('sv-SE', options).format(date); // YYYY-MM-DD HH:mm:ss
      
    case 'display':
    default:
      options.year = 'numeric';
      options.month = 'short';
      options.day = 'numeric';
      options.hour = 'numeric';
      options.minute = '2-digit';
      options.hour12 = true;
      return new Intl.DateTimeFormat('en-US', options).format(date); // Aug 11, 2024, 9:30 AM
  }
}

/**
 * Get today's date in user's timezone as YYYY-MM-DD
 * 
 * @param userTimezone - User's timezone
 * @returns Date string (e.g., "2024-08-11")
 */
export function getTodayInTimezone(userTimezone: string): string {
  return fromUTC(nowUTC(), userTimezone, 'date');
}

/**
 * Format time according to user preference (12h/24h)
 * 
 * @param utcISOString - UTC ISO string
 * @param userTimezone - User's timezone
 * @param timeFormat - User's time format preference
 * @returns Formatted time string
 * 
 * @example
 * formatTime("2024-08-11T13:30:00.000Z", "America/New_York", "12h")
 * // Returns: "9:30 AM"
 * 
 * formatTime("2024-08-11T13:30:00.000Z", "America/New_York", "24h")
 * // Returns: "09:30"
 */
export function formatTime(
  utcISOString: string, 
  userTimezone: string, 
  timeFormat: '12h' | '24h'
): string {
  const date = new Date(utcISOString);
  
  if (!isValidDate(date)) {
    throw new Error(`Invalid ISO string: ${utcISOString}`);
  }
  
  const options: Intl.DateTimeFormatOptions = {
    timeZone: userTimezone,
    hour: 'numeric',
    minute: '2-digit'
  };
  
  if (timeFormat === '12h') {
    options.hour12 = true;
    return new Intl.DateTimeFormat('en-US', options).format(date);
  } else {
    options.hour12 = false;
    return new Intl.DateTimeFormat('en-GB', options).format(date);
  }
}

/**
 * Check if current time is within trading hours
 * 
 * @param tradingSession - Session configuration from settings
 * @param userTimezone - User's timezone
 * @param currentTime - Optional specific time to check (defaults to now)
 * @returns Object with status and time information
 * 
 * @example
 * const session = { start: "09:30", end: "16:00" };
 * isWithinTradingHours(session, "America/New_York")
 * // Returns: { 
 * //   isOpen: true, 
 * //   minutesUntilClose: 45,
 * //   status: "open" 
 * // }
 */
export function isWithinTradingHours(
  tradingSession: { start: string; end: string }, 
  userTimezone: string,
  currentTime?: string
): {
  isOpen: boolean;
  status: 'premarket' | 'open' | 'closed';
  minutesUntilOpen?: number;
  minutesUntilClose?: number;
  nextOpenTime?: string;
  nextCloseTime?: string;
} {
  const now = currentTime ? new Date(currentTime) : new Date();
  const today = getTodayInTimezone(userTimezone);
  
  // Create today's session times in user timezone
  const sessionStart = toUTC(`${today} ${tradingSession.start}:00`, userTimezone);
  const sessionEnd = toUTC(`${today} ${tradingSession.end}:00`, userTimezone);
  
  const startTime = new Date(sessionStart);
  const endTime = new Date(sessionEnd);
  const currentUTC = now.getTime();
  
  if (currentUTC < startTime.getTime()) {
    // Before market open
    const minutesUntilOpen = Math.ceil((startTime.getTime() - currentUTC) / (1000 * 60));
    return {
      isOpen: false,
      status: 'premarket',
      minutesUntilOpen,
      nextOpenTime: sessionStart
    };
  } else if (currentUTC >= startTime.getTime() && currentUTC < endTime.getTime()) {
    // Market is open
    const minutesUntilClose = Math.ceil((endTime.getTime() - currentUTC) / (1000 * 60));
    return {
      isOpen: true,
      status: 'open',
      minutesUntilClose,
      nextCloseTime: sessionEnd
    };
  } else {
    // Market is closed
    // Calculate next open (tomorrow)
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowDate = fromUTC(tomorrow.toISOString(), userTimezone, 'date');
    const nextOpen = toUTC(`${tomorrowDate} ${tradingSession.start}:00`, userTimezone);
    const minutesUntilOpen = Math.ceil((new Date(nextOpen).getTime() - currentUTC) / (1000 * 60));
    
    return {
      isOpen: false,
      status: 'closed',
      minutesUntilOpen,
      nextOpenTime: nextOpen
    };
  }
}

/**
 * Format duration in minutes to human readable string
 * 
 * @param minutes - Duration in minutes
 * @returns Formatted string (e.g., "2h 15m", "45m", "1h")
 */
export function formatDuration(minutes: number): string {
  if (minutes < 1) {
    return "< 1m";
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (hours === 0) {
    return `${remainingMinutes}m`;
  } else if (remainingMinutes === 0) {
    return `${hours}h`;
  } else {
    return `${hours}h ${remainingMinutes}m`;
  }
}

/**
 * Get time until next market event with human readable formatting
 * 
 * @param tradingSession - Session configuration
 * @param userTimezone - User's timezone
 * @returns Formatted string for display
 * 
 * @example
 * getTimeUntilMarketEvent(session, "America/New_York")
 * // Returns: "Market opens in 2h 15m" or "Market closes in 45m"
 */
export function getTimeUntilMarketEvent(
  tradingSession: { start: string; end: string },
  userTimezone: string
): string {
  const status = isWithinTradingHours(tradingSession, userTimezone);
  
  if (status.status === 'premarket' && status.minutesUntilOpen) {
    return `Market opens in ${formatDuration(status.minutesUntilOpen)}`;
  } else if (status.status === 'open' && status.minutesUntilClose) {
    return `Market closes in ${formatDuration(status.minutesUntilClose)}`;
  } else if (status.status === 'closed' && status.minutesUntilOpen) {
    const hours = Math.floor(status.minutesUntilOpen / 60);
    if (hours < 24) {
      return `Market opens in ${formatDuration(status.minutesUntilOpen)}`;
    } else {
      return `Market opens tomorrow`;
    }
  }
  
  return 'Market status unknown';
}

/**
 * Validate if a date is valid
 * 
 * @param date - Date object to validate
 * @returns Boolean indicating if date is valid
 */
export function isValidDate(date: Date): boolean {
  return date instanceof Date && !isNaN(date.getTime());
}

/**
 * Parse various date input formats into a valid Date object
 * 
 * @param input - Date string in various formats
 * @returns Date object or null if parsing fails
 * 
 * @example
 * parseDate("2024-08-11") // Date object
 * parseDate("08/11/2024") // Date object  
 * parseDate("Aug 11, 2024") // Date object
 * parseDate("invalid") // null
 */
export function parseDate(input: string): Date | null {
  if (!input || typeof input !== 'string') {
    return null;
  }
  
  // Try parsing various formats
  const formats = [
    input, // Original format
    input.replace(/(\d{1,2})\/(\d{1,2})\/(\d{4})/, '$3-$1-$2'), // MM/dd/yyyy → yyyy-MM-dd
    input.replace(/(\d{1,2})-(\d{1,2})-(\d{4})/, '$3-$1-$2'), // MM-dd-yyyy → yyyy-MM-dd
  ];
  
  for (const format of formats) {
    const date = new Date(format);
    if (isValidDate(date)) {
      return date;
    }
  }
  
  return null;
}

/**
 * Compare two dates ignoring time (date only)
 * 
 * @param date1 - First date (ISO string or Date)
 * @param date2 - Second date (ISO string or Date)
 * @param userTimezone - Timezone for comparison
 * @returns -1 if date1 < date2, 0 if equal, 1 if date1 > date2
 */
export function compareDatesOnly(
  date1: string | Date, 
  date2: string | Date, 
  userTimezone: string
): number {
  const d1 = typeof date1 === 'string' ? new Date(date1) : date1;
  const d2 = typeof date2 === 'string' ? new Date(date2) : date2;
  
  const date1Str = fromUTC(d1.toISOString(), userTimezone, 'date');
  const date2Str = fromUTC(d2.toISOString(), userTimezone, 'date');
  
  if (date1Str < date2Str) return -1;
  if (date1Str > date2Str) return 1;
  return 0;
}

/**
 * Get timezone offset in milliseconds for a specific date
 * Helper function for timezone calculations
 */
function getTimezoneOffset(timezone: string, date: Date): number {
  try {
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const timezoneDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
    return utcDate.getTime() - timezoneDate.getTime();
  } catch (error) {
    console.warn(`Failed to calculate timezone offset for ${timezone}:`, error);
    return 0;
  }
}

/**
 * Common timezone abbreviations for display
 */
export const TIMEZONE_ABBREVIATIONS: Record<string, string> = {
  'America/New_York': 'ET',
  'America/Chicago': 'CT', 
  'America/Denver': 'MT',
  'America/Los_Angeles': 'PT',
  'Europe/London': 'GMT',
  'Europe/Paris': 'CET',
  'Asia/Tokyo': 'JST',
  'Asia/Shanghai': 'CST',
  'Australia/Sydney': 'AEDT'
};

/**
 * Get the user's configured display timezone from BasicSettings
 * This is the timezone used for ALL date/time display in the app
 * 
 * @param basicSettings - User's basic settings
 * @returns Display timezone string (e.g., "America/New_York")
 * 
 * @example
 * const settings = await basicSettingsService.get();
 * const displayTz = getDisplayTimezone(settings);
 * const sessionDate = getTodayInTimezone(displayTz);
 */
export function getDisplayTimezone(basicSettings: { displayTimezone: string }): string {
  return basicSettings.displayTimezone;
}

/**
 * Get the browser's local timezone for comparison/debugging
 * NOTE: This is NOT used for display - only for debugging timezone differences
 * 
 * @returns Browser's detected timezone (e.g., "America/Los_Angeles")
 * 
 * @example
 * const browserTz = getBrowserTimezone();      // "America/Los_Angeles" 
 * const displayTz = settings.displayTimezone; // "America/New_York"
 * // User in CA viewing times in ET - this is totally valid!
 */
export function getBrowserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Get timezone abbreviation for display
 *
 * @param timezone - Full timezone name
 * @returns Abbreviated timezone (e.g., "ET", "PT")
 */
export function getTimezoneAbbr(timezone: string): string {
  return TIMEZONE_ABBREVIATIONS[timezone] || timezone.split('/').pop() || timezone;
}

/**
 * Convert time-only string from user's timezone to UTC
 * Used for storing trading session times consistently in UTC
 *
 * @param timeStr - Time string in HH:mm format (e.g., "09:30")
 * @param timezone - User's timezone (e.g., "America/New_York")
 * @returns UTC time string in HH:mm format (e.g., "13:30")
 *
 * @example
 * timeStringToUTC("09:30", "America/New_York") // Returns: "13:30" (during EST)
 * timeStringToUTC("09:30", "America/New_York") // Returns: "14:30" (during EDT)
 */
export function timeStringToUTC(timeStr: string, timezone: string): string {
  const [hours, minutes] = timeStr.split(':').map(Number);

  if (hours === undefined || minutes === undefined || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    throw new Error(`Invalid time string: ${timeStr}. Expected HH:mm format.`);
  }

  // Create a date object for today at the specified time in the user's timezone
  const now = new Date();
  const localDate = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
  localDate.setHours(hours, minutes, 0, 0);

  // Parse the local time and get UTC equivalent
  const year = localDate.getFullYear();
  const month = String(localDate.getMonth() + 1).padStart(2, '0');
  const day = String(localDate.getDate()).padStart(2, '0');

  const localDateStr = `${year}-${month}-${day}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
  const utcDate = new Date(localDateStr + getTimezoneOffsetString(timezone, localDate));

  const utcHours = utcDate.getUTCHours();
  const utcMinutes = utcDate.getUTCMinutes();

  return `${String(utcHours).padStart(2, '0')}:${String(utcMinutes).padStart(2, '0')}`;
}

/**
 * Convert UTC time-only string to user's timezone
 * Used for displaying stored trading session times in user's preferred timezone
 *
 * @param utcTimeStr - UTC time string in HH:mm format (e.g., "13:30")
 * @param timezone - User's timezone (e.g., "America/New_York")
 * @returns Local time string in HH:mm format (e.g., "09:30")
 *
 * @example
 * timeStringFromUTC("13:30", "America/New_York") // Returns: "09:30" (during EST)
 * timeStringFromUTC("14:30", "America/New_York") // Returns: "09:30" (during EDT)
 */
export function timeStringFromUTC(utcTimeStr: string, timezone: string): string {
  const [hours, minutes] = utcTimeStr.split(':').map(Number);

  if (hours === undefined || minutes === undefined || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    throw new Error(`Invalid UTC time string: ${utcTimeStr}. Expected HH:mm format.`);
  }

  // Create a UTC date for today at the specified UTC time
  const now = new Date();
  const utcDate = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
    hours,
    minutes,
    0,
    0
  ));

  // Format in user's timezone
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });

  return formatter.format(utcDate); // Returns HH:mm
}

/**
 * Format UTC time-only string according to user preferences
 * Converts from UTC to user's timezone and applies 12h/24h formatting
 *
 * @param utcTimeStr - UTC time string in HH:mm format (e.g., "13:30")
 * @param timezone - User's timezone (e.g., "America/New_York")
 * @param format - User's time format preference ('12h' | '24h')
 * @returns Formatted time string (e.g., "9:30 AM" or "09:30")
 *
 * @example
 * formatTimeString("13:30", "America/New_York", "12h") // Returns: "9:30 AM"
 * formatTimeString("13:30", "America/New_York", "24h") // Returns: "09:30"
 */
export function formatTimeString(
  utcTimeStr: string,
  timezone: string,
  format: '12h' | '24h'
): string {
  const [hours, minutes] = utcTimeStr.split(':').map(Number);

  if (hours === undefined || minutes === undefined || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    throw new Error(`Invalid UTC time string: ${utcTimeStr}. Expected HH:mm format.`);
  }

  // Create a UTC date for today at the specified UTC time
  const now = new Date();
  const utcDate = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
    hours,
    minutes,
    0,
    0
  ));

  // Format according to user preferences
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: format === '12h'
  });

  return formatter.format(utcDate);
}

/**
 * Helper function to get timezone offset string for date conversion
 * @private
 */
function getTimezoneOffsetString(timezone: string, date: Date): string {
  try {
    // Get the offset in minutes
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
    const offsetMs = tzDate.getTime() - utcDate.getTime();
    const offsetMinutes = offsetMs / (1000 * 60);
    const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
    const offsetMins = Math.abs(offsetMinutes) % 60;
    const sign = offsetMinutes >= 0 ? '+' : '-';

    return `${sign}${String(offsetHours).padStart(2, '0')}:${String(offsetMins).padStart(2, '0')}`;
  } catch (error) {
    console.warn(`Failed to get timezone offset for ${timezone}:`, error);
    return '+00:00';
  }
}

/**
 * Add or subtract days from a YYYY-MM-DD date string
 *
 * @param dateStr - Date string in YYYY-MM-DD format
 * @param days - Number of days to add (negative to subtract)
 * @returns New date string in YYYY-MM-DD format
 *
 * @example
 * addDays("2026-01-15", 1)  // Returns: "2026-01-16"
 * addDays("2026-01-15", -1) // Returns: "2026-01-14"
 * addDays("2026-01-01", -1) // Returns: "2025-12-31"
 */
export function addDays(dateStr: string, days: number): string {
  const parts = dateStr.split('-').map(Number);
  const year = parts[0];
  const month = parts[1];
  const day = parts[2];

  if (year === undefined || month === undefined || day === undefined) {
    throw new Error(`Invalid date string: ${dateStr}. Expected YYYY-MM-DD format.`);
  }

  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  const result = date.toISOString().split('T')[0];

  if (!result) {
    throw new Error(`Failed to compute date from: ${dateStr}`);
  }

  return result;
}

/**
 * Module-level cache for Intl.DateTimeFormat instances
 * Keyed by timezone string for performance
 */
const dateFormatterCache = new Map<string, Intl.DateTimeFormat>();

/**
 * Get cached DateTimeFormat for extracting YYYY-MM-DD in a timezone
 */
function getDateFormatter(timezone: string): Intl.DateTimeFormat {
  let formatter = dateFormatterCache.get(timezone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-CA', { timeZone: timezone });
    dateFormatterCache.set(timezone, formatter);
  }
  return formatter;
}

/**
 * Check if an event's datetime falls on the session date in the user's display timezone
 *
 * Uses the user's configured display timezone (from settings), NOT browser local time.
 * A trader in California with display timezone set to "America/New_York" will see
 * events grouped by Eastern Time dates.
 *
 * @param datetime - Event's UTC datetime string (ISO format)
 * @param sessionDate - Session date in YYYY-MM-DD format
 * @param displayTimezone - User's display timezone (e.g., "America/New_York")
 * @returns true if the event falls on the session date in display timezone
 *
 * @example
 * // Event at 3 AM UTC on Jan 15 is still Jan 14 in EST (UTC-5)
 * isEventOnDate("2026-01-15T03:00:00Z", "2026-01-14", "America/New_York") // true
 * isEventOnDate("2026-01-15T03:00:00Z", "2026-01-15", "America/New_York") // false
 *
 * // Event at 10 AM UTC on Jan 15 is Jan 15 in EST (5 AM local)
 * isEventOnDate("2026-01-15T10:00:00Z", "2026-01-15", "America/New_York") // true
 */
export function isEventOnDate(
  datetime: string | undefined,
  sessionDate: string,
  displayTimezone: string
): boolean {
  // Guard: missing datetime
  if (!datetime) {
    return false;
  }

  // Parse the datetime
  const eventDate = new Date(datetime);

  // Guard: invalid datetime
  if (!Number.isFinite(eventDate.getTime())) {
    return false;
  }

  // Get the event's date in the user's display timezone (YYYY-MM-DD format)
  const formatter = getDateFormatter(displayTimezone);
  const eventLocalDate = formatter.format(eventDate);

  return eventLocalDate === sessionDate;
}

/**
 * Check if user's display timezone differs from browser timezone
 * Useful for showing timezone awareness in UI
 *
 * @param displayTimezone - User's configured display timezone
 * @returns Object with timezone information
 *
 * @example
 * const info = getTimezoneInfo("America/New_York");
 * if (info.isDifferent) {
 *   showInfo(`Displaying times in ${info.displayAbbr}, your local time is ${info.browserAbbr}`);
 * }
 */
export function getTimezoneInfo(displayTimezone: string): {
  browserTimezone: string;
  displayTimezone: string;
  browserAbbr: string;
  displayAbbr: string;
  isDifferent: boolean;
} {
  const browserTimezone = getBrowserTimezone();
  
  return {
    browserTimezone,
    displayTimezone,
    browserAbbr: getTimezoneAbbr(browserTimezone),
    displayAbbr: getTimezoneAbbr(displayTimezone),
    isDifferent: browserTimezone !== displayTimezone
  };
}